/*
 * The thread's "you're the blocker" cues: the waiting line atop an approval
 * card, the sticky "Approval Needed" pill above the composer while that card
 * is out of view, and the composer's paused note.
 */
import { ArrowUp } from "lucide-react";
import { useEffect, useState, type FC } from "react";

import { StatusIcon } from "@/components/ui/status-icon";
import { cn } from "@/lib/utils";

const firstSeen = new Map<string, number>();

/** When this approval was first shown in this session. */
export const waitingSince = (id: string, now = Date.now()): number => {
  const at = firstSeen.get(id);
  if (at !== undefined) return at;
  firstSeen.set(id, now);
  return now;
};

export const waitLabel = (ms: number): string => {
  const min = Math.floor(ms / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  return `${h} hr${h === 1 ? "" : "s"}${min % 60 ? ` ${min % 60} min` : ""}`;
};

const useNow = (everyMs: number): number => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), everyMs);
    return () => clearInterval(t);
  }, [everyMs]);
  return now;
};

export const WaitingLine: FC<{ approvalId: string }> = ({ approvalId }) => {
  const now = useNow(30_000);
  const since = waitingSince(approvalId);
  const waited = Math.max(0, now - since);
  return (
    <div
      data-testid="approval-waiting"
      className="flex items-center gap-1.5 text-sm text-fg-3"
    >
      <StatusIcon status="needs-you" aria-hidden />
      <span className="font-medium text-warn">Waiting on you</span>
      <span>· {waited < 60_000 ? "just now" : `for ${waitLabel(waited)}`}</span>
    </div>
  );
};

export const approvalSelector = (id: string) =>
  `[data-approval-id="${CSS.escape(id)}"]`;

/** Shown above the composer while the pending approval card is scrolled out of view. */
export const ApprovalNeededPill: FC<{ approvalId: string }> = ({
  approvalId
}) => {
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let observer: IntersectionObserver | null = null;
    let frame = 0;
    const attach = () => {
      const el = document.querySelector(approvalSelector(approvalId));
      if (!el) {
        frame = requestAnimationFrame(attach);
        return;
      }
      if (typeof IntersectionObserver === "undefined") return;
      observer = new IntersectionObserver(
        ([entry]) => setHidden(!entry.isIntersecting),
        { threshold: 0.2 }
      );
      observer.observe(el);
    };
    attach();
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
    };
  }, [approvalId]);

  if (!hidden) return null;
  return (
    <div className="pointer-events-none flex justify-center">
      <button
        type="button"
        data-testid="approval-needed-pill"
        onClick={() =>
          document
            .querySelector(approvalSelector(approvalId))
            ?.scrollIntoView({ behavior: "smooth", block: "center" })
        }
        className="signal-fade pointer-events-auto flex h-6 items-center gap-1.5 rounded-full bg-elevated px-2.5 text-sm font-medium text-fg-2 shadow-sm ring-1 ring-line-2 hover:bg-fill-1"
      >
        <StatusIcon status="needs-you" aria-hidden className="size-3" />
        Approval Needed
        <ArrowUp className="size-3 text-fg-3" />
      </button>
    </div>
  );
};

export const PausedNote: FC<{ className?: string }> = ({ className }) => (
  <p
    data-testid="composer-paused-note"
    className={cn("px-1 text-sm text-fg-3", className)}
  >
    Paused for your approval above. Sending a message declines that step.
  </p>
);
